import {
  Badge,
  Box,
  Button,
  CloseButton,
  Flex,
  Group,
  Loader,
  Progress,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IChecklistItemRow } from "@mctypes/checklist.types";
import { IconSortAscendingNumbers } from "@tabler/icons-react";
import { ColDef, ColumnVisibleEvent, RowClassParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  useDeleteProjectByProjectIdChecklistAndChecklistIdCategoryCategoryIdMutation,
  useDeleteProjectByProjectIdChecklistAndChecklistIdItemItemIdMutation,
  usePostChecklistIntegrationGenerateMutation,
  usePostProjectByProjectIdChecklistAndChecklistIdCategoryMutation,
  usePostProjectByProjectIdChecklistAndChecklistIdItemMutation,
  usePutProjectByProjectIdChecklistAndChecklistIdCategoryCategoryIdInactiveMutation,
  usePutProjectByProjectIdChecklistAndChecklistIdCategoryCategoryIdMutation,
  usePutProjectByProjectIdChecklistAndChecklistIdItemItemIdMutation,
  usePutProjectByProjectIdChecklistAndChecklistIdMutation,
  usePutProjectByProjectIdChecklistAndChecklistIdUpdateChecklistIdsMutation,
} from "../../../../../openapi";
import { AG_GRID_LOCALE_NOR } from "../../../../agGridLocale";
import { CreateNewButton, ExcelExportDropdown } from "../../../../components";
import { selectCurrentProjectId } from "../../../../global/globalSliceCommon";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { STANDARD_COLORS } from "../../../globalConstants";
import { selectIsTemplateProject } from "../../../projectSelectors";
import "../checklistBoardApi.enhance";
import {
  selectActiveChecklist,
  selectActiveChecklistCheckKeysForPage,
  selectChecklistCategories,
  selectChecklistCategoryCollapseStateForPage,
  selectChecklistDisplayRowsForPage,
  selectChecklistHiddenColumns,
  selectChecklistItemCountByCategoryIdForPage,
  selectChecklistItems,
  selectChecklistProgressSegmentsForPage,
  selectLatestChecklistChangeLabelForPage,
  selectRefactoredChecklistEffectiveEditMode,
  selectRefactoredCollapsedCategoryIds,
  selectRefactoredExpandedCategoryIds,
} from "../checklistSelectors";
import { setRefactoredEditModeInRedux, toggleColVisibility } from "../checklistSlice";
import { ChecklistSummaryTable } from "../ChecklistSummaryTable";
import { thunkToggleAllRefactoredCategoriesCollapse, thunkToggleRefactoredCategoryCollapse } from "../checklistThunk";
import { getCategoryCount } from "../checklistUtils";
import ExportChecklistButton from "../ExportTemplateButton";
import InitialsAndDate from "../InitialsAndDate";
import { DEFAULT_CHECKLIST_COL_DEF, getChecklistColumns } from "./checklistColumns";
import ChecklistModalRefactored from "./ChecklistModalRefactored";
import "./ChecklistTableRefactored.css";
import { CHECKLIST_TABLE_SIDE_BAR, getChecklistExportColumnKeys } from "./checklistTableRefactorUtils";
import { useChecklistRowDrag } from "./useChecklistRowDrag";

const ChecklistBoardAgGridRefactored: React.FC = () => {
  const dispatch = useAppDispatch();
  const { checklistId } = useParams<{ checklistId: string }>();
  const routerHistory = useHistory();
  const location = useLocation();
  const selectedChecklistRowId = useMemo(() => new URLSearchParams(location.search).get("id"), [location.search]);
  const checklist = useAppSelector((state) => selectActiveChecklist(state, checklistId));
  const checklistCategories = useAppSelector((state) => selectChecklistCategories(state, checklistId));
  const checklistItems = useAppSelector((state) => selectChecklistItems(state, checklistId));
  const rows = useAppSelector((state) => selectChecklistDisplayRowsForPage(state, checklistId));
  const activeCheckKeys = useAppSelector((state) => selectActiveChecklistCheckKeysForPage(state, checklistId));
  const progressSegments = useAppSelector((state) => selectChecklistProgressSegmentsForPage(state, checklistId));
  const hiddenColumns = useAppSelector(selectChecklistHiddenColumns);
  const collapseState = useAppSelector((state) => selectChecklistCategoryCollapseStateForPage(state, checklistId));
  const refactoredCollapsedCategoryIds = useAppSelector(selectRefactoredCollapsedCategoryIds);
  const refactoredExpandedCategoryIds = useAppSelector(selectRefactoredExpandedCategoryIds);
  const projectId = useAppSelector(selectCurrentProjectId) ?? "";
  const signedBy = useAppSelector((state) => state.globalReducer.user);
  const isTemplateProject = useAppSelector(selectIsTemplateProject);
  const latestChecklistChange = useAppSelector((state) => selectLatestChecklistChangeLabelForPage(state, checklistId));
  const itemCountByCategoryId = useAppSelector((state) =>
    selectChecklistItemCountByCategoryIdForPage(state, checklistId),
  );
  const effectiveEditMode = useAppSelector((state) => selectRefactoredChecklistEffectiveEditMode(state, checklistId));
  const gridRef = useRef<AgGridReact<IChecklistItemRow>>(null);
  const [filterText, setFilterText] = useState("");
  const [showStats, setShowStats] = useState(false);
  const [addCategoryMutation] = usePostProjectByProjectIdChecklistAndChecklistIdCategoryMutation();
  const [addItemMutation] = usePostProjectByProjectIdChecklistAndChecklistIdItemMutation();
  const [deleteCategoryMutation] = useDeleteProjectByProjectIdChecklistAndChecklistIdCategoryCategoryIdMutation();
  const [deleteItemMutation] = useDeleteProjectByProjectIdChecklistAndChecklistIdItemItemIdMutation();
  const [updateCategoryMutation] = usePutProjectByProjectIdChecklistAndChecklistIdCategoryCategoryIdMutation();
  const [toggleCategoryInactiveMutation] =
    usePutProjectByProjectIdChecklistAndChecklistIdCategoryCategoryIdInactiveMutation();
  const [updateItemMutation] = usePutProjectByProjectIdChecklistAndChecklistIdItemItemIdMutation();
  const [updateChecklistMutation] = usePutProjectByProjectIdChecklistAndChecklistIdMutation();
  const [updateChecklistIdsMutation] = usePutProjectByProjectIdChecklistAndChecklistIdUpdateChecklistIdsMutation();
  const [generateChecklistReport, { isLoading: generateChecklistReportLoading }] =
    usePostChecklistIntegrationGenerateMutation();
  const { onRowDragEnd, onRowDragEnter, restoreCategoryDragCollapseState } = useChecklistRowDrag({
    checklistObjectId: checklist?._id,
    collapseState,
    gridRef,
    projectId,
    refactoredCollapsedCategoryIds,
    refactoredExpandedCategoryIds,
    rows,
  });

  const handleToggleColumnVisibility = useCallback(
    (event: ColumnVisibleEvent<IChecklistItemRow>) => {
      if (event.source !== "toolPanelUi" || event.columns == null || event.visible == null) {
        return;
      }

      const toggledColumnIds = event.columns
        .map((column) => column.getColId())
        .filter((columnId) => columnId !== "expand" && columnId !== "edit-actions");

      if (toggledColumnIds.length === 0) {
        return;
      }

      dispatch(toggleColVisibility({ keys: toggledColumnIds, bool: event.visible }));
    },
    [dispatch],
  );

  const setSelectedRowAndAddIdToUrl = useCallback(
    (row: IChecklistItemRow) => {
      routerHistory.push(`${location.pathname}?id=${row._id}`);
    },
    [location.pathname, routerHistory],
  );

  const excelExportColumns = useCallback(
    (allColumns: boolean) => {
      const api = gridRef.current?.api;
      if (api == null || checklist == null) {
        return;
      }

      const fileName = `${checklist.id}_${checklist.name}.xlsx`;
      const columns = allColumns ? api.getColumns() : api.getAllDisplayedColumns();
      const columnKeys = getChecklistExportColumnKeys(columns?.map((column) => column.getId()) ?? []);

      api.exportDataAsExcel({
        fileName,
        sheetName: fileName,
        columnKeys,
      });
    },
    [checklist],
  );

  const columns = useMemo<ColDef<IChecklistItemRow>[]>(
    () =>
      checklist == null
        ? []
        : getChecklistColumns(checklist, activeCheckKeys, hiddenColumns, {
            allCategoriesCollapsed: collapseState.allCategoriesCollapsed,
            collapsedCategoryIds: collapseState.collapsedCategoryIds,
            editMode: effectiveEditMode,
            onAddItem: (categoryId) =>
              void addItemMutation({
                projectId,
                checklistId: checklist._id,
                body: {
                  categoryId,
                  itemCount: itemCountByCategoryId[categoryId] ?? 0,
                },
              }),
            onDeleteCategory: (categoryId) =>
              void deleteCategoryMutation({ projectId, checklistId: checklist._id, categoryId }),
            onDeleteItem: (itemId) => void deleteItemMutation({ projectId, checklistId: checklist._id, itemId }),
            onOpenRow: setSelectedRowAndAddIdToUrl,
            onToggleAllCategories: () =>
              dispatch(
                thunkToggleAllRefactoredCategoriesCollapse(
                  collapseState,
                  refactoredCollapsedCategoryIds,
                  refactoredExpandedCategoryIds,
                ),
              ),
            onToggleCategory: (categoryId) =>
              dispatch(
                thunkToggleRefactoredCategoryCollapse(
                  categoryId,
                  collapseState,
                  refactoredCollapsedCategoryIds,
                  refactoredExpandedCategoryIds,
                ),
              ),
            onToggleInactive: (row) => {
              const nextValue = row.inactive?.isChecked !== true;

              if (row.originalItem != null) {
                void updateItemMutation({
                  projectId,
                  checklistId: checklist._id,
                  itemId: row._id,
                  body: {
                    path: "inactive.isChecked",
                    oldValue: row.inactive?.isChecked?.toString() ?? "",
                    newValue: nextValue,
                  },
                });
                return;
              }

              void toggleCategoryInactiveMutation({
                projectId,
                checklistId: checklist._id,
                categoryId: row._id,
                body: {
                  inactive: nextValue,
                },
              });
            },
            onUpdateCategory: (categoryId, attribute, oldValue, newValue) =>
              void updateCategoryMutation({
                projectId,
                checklistId: checklist._id,
                categoryId,
                body: {
                  attribute,
                  oldValue,
                  newValue,
                },
              }),
            onUpdateItem: (itemId, path, oldValue, newValue) =>
              void updateItemMutation({
                projectId,
                checklistId: checklist._id,
                itemId,
                body: {
                  path,
                  oldValue,
                  newValue,
                },
              }),
          }),
    [
      activeCheckKeys,
      addItemMutation,
      checklist,
      collapseState,
      deleteCategoryMutation,
      deleteItemMutation,
      dispatch,
      effectiveEditMode,
      hiddenColumns,
      itemCountByCategoryId,
      projectId,
      refactoredCollapsedCategoryIds,
      refactoredExpandedCategoryIds,
      setSelectedRowAndAddIdToUrl,
      toggleCategoryInactiveMutation,
      updateCategoryMutation,
      updateItemMutation,
    ],
  );

  if (checklist == null) {
    return (
      <Group justify="center" flex={1} p="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="xs" h="100%" mih={0}>
      <Flex m="0.5rem">
        <Flex component="h3" align="center" m={0}>
          <Box mx="0.5rem">{checklist.name}</Box>
        </Flex>
        {effectiveEditMode === false && (
          <Flex justify="flex-end" align="center" style={{ flexGrow: 1 }}>
            <Box w="100%" style={{ maxWidth: "40%", minWidth: 260 }}>
              <Text size="xs" c="dimmed" ta="right" mb={3}>
                {latestChecklistChange != null ? `Sist endret: ${latestChecklistChange}` : `${rows.length} rader`}
              </Text>
              <Progress.Root h={16} radius={4} style={{ width: "100%" }}>
                <Progress.Section value={progressSegments.accepted} color={STANDARD_COLORS.green} />
                <Progress.Section value={progressSegments.rejected} color={STANDARD_COLORS.red} />
              </Progress.Root>
            </Box>
          </Flex>
        )}
      </Flex>

      <Flex align="center" gap="xs" wrap="wrap" mx="0.5rem" mb="0.5rem">
        {effectiveEditMode === true && (
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Tilbakestill ID-sekvenser" withArrow openDelay={300}>
              <Button
                color="mc-primary"
                size="sm"
                variant="outline"
                onClick={() => void updateChecklistIdsMutation({ projectId, checklistId: checklist._id })}
                leftSection={<IconSortAscendingNumbers size={16} />}
              >
                ID
              </Button>
            </Tooltip>
            <CreateNewButton
              onClick={() =>
                void addCategoryMutation({
                  projectId,
                  checklistId: checklist._id,
                  body: {
                    categoryCount: getCategoryCount(checklistCategories),
                  },
                })
              }
              size="sm"
              tooltip="Legg til kategori"
            />
          </Group>
        )}
        <TextInput
          size="sm"
          placeholder="Søk..."
          value={filterText}
          onChange={(event) => setFilterText(event.currentTarget.value)}
          w={170}
          rightSection={filterText.length > 0 && <CloseButton onClick={() => setFilterText("")} size="sm" />}
        />

        {checklist.itemTagsActive === true && (
          <Group align="center" gap="xs" wrap="nowrap">
            <TagToggle
              tags={checklist.itemTags ?? []}
              currentTags={checklist.activeItemTags ?? []}
              onChange={(tags) =>
                void updateChecklistMutation({
                  projectId,
                  checklistId: checklist._id,
                  body: {
                    attribute: "activeItemTags",
                    oldValue: checklist.activeItemTags ?? [],
                    newValue: tags,
                  },
                })
              }
            />
            <InitialsAndDate
              updatedBy={checklist.activeItemTagChangedBy}
              updatedDate={checklist.activeItemTagChangedDate}
              singleLine={true}
            />
          </Group>
        )}

        {effectiveEditMode === false && (
          <Group gap="xs" wrap="nowrap" style={{ marginLeft: "auto" }}>
            <Button color="lightgray.9" size="sm-compact" variant="outline" onClick={() => setShowStats(!showStats)}>
              <i className="fa fa-bar-chart fa-fw" />
              Vis statistikk
            </Button>
            <Button
              color="lightgray.9"
              size="sm-compact"
              variant="outline"
              loading={generateChecklistReportLoading}
              leftSection={<i className="fa fa-file-word-o fa-fw" />}
              onClick={() =>
                checklist.id != null &&
                signedBy != null &&
                void generateChecklistReport({
                  body: {
                    checklistId: checklist.id,
                    signedBy: signedBy.name || signedBy._id,
                  },
                })
              }
            >
              Generer utskrift
            </Button>
            <ExcelExportDropdown
              buttonVariant="outline"
              excelExportVisibleColumns={() => excelExportColumns(false)}
              exportExcelAllColumns={() => excelExportColumns(true)}
            />
            <Button
              variant="filled"
              color="mc-primary"
              size="sm-compact"
              leftSection={<i className="fa fa-pencil fa-fw" />}
              onClick={() => {
                dispatch(
                  thunkToggleAllRefactoredCategoriesCollapse(
                    { ...collapseState, allCategoriesCollapsed: true },
                    refactoredCollapsedCategoryIds,
                    refactoredExpandedCategoryIds,
                  ),
                );
                dispatch(setRefactoredEditModeInRedux(true));
              }}
            >
              Rediger sjekkliste
            </Button>
          </Group>
        )}

        {effectiveEditMode === true && (
          <Group gap="xs" wrap="nowrap" style={{ marginLeft: "auto" }}>
            {isTemplateProject && <ExportChecklistButton checklistLocalId={checklist.id} />}
            {checklist.isTemplate === false && (
              <Button
                color="green"
                size="sm-compact"
                variant="filled"
                leftSection={<i className="fa fa-check fa-fw" />}
                onClick={() => dispatch(setRefactoredEditModeInRedux(false))}
              >
                Lukk redigering
              </Button>
            )}
          </Group>
        )}
      </Flex>

      {showStats && (
        <Box className="bg-light" p="1rem">
          <ChecklistSummaryTable checklist={checklist} items={checklistItems} />
        </Box>
      )}

      <Flex className="border" direction="column" h="100%" style={{ overflow: "auto" }}>
        <Box className="checklistTable ag-theme-quartz" h="100%" w="100%">
          <AgGridReact<IChecklistItemRow>
            ref={gridRef}
            rowData={rows}
            columnDefs={columns}
            defaultColDef={DEFAULT_CHECKLIST_COL_DEF}
            getRowId={(params) => params.data._id}
            getRowClass={(params: RowClassParams<IChecklistItemRow>) =>
              params.data?.originalItem == null ? "checklist-refactored-category-row" : undefined
            }
            localeText={AG_GRID_LOCALE_NOR}
            quickFilterText={filterText}
            rowHeight={53}
            stopEditingWhenCellsLoseFocus
            animateRows
            suppressCutToClipboard
            suppressMoveWhenRowDragging
            enableCellTextSelection={effectiveEditMode === false}
            rowDragManaged
            onRowDragEnter={onRowDragEnter}
            onRowDragEnd={onRowDragEnd}
            onRowDragLeave={restoreCategoryDragCollapseState}
            sideBar={effectiveEditMode !== true ? CHECKLIST_TABLE_SIDE_BAR : null}
            onColumnVisible={handleToggleColumnVisibility}
          />
        </Box>
      </Flex>

      {selectedChecklistRowId != null && <ChecklistModalRefactored />}
    </Stack>
  );
};

export default ChecklistBoardAgGridRefactored;

const TagToggle: React.FC<{ tags: string[]; currentTags: string[]; onChange: (tags: string[]) => void }> = ({
  tags = [],
  currentTags = [],
  onChange,
}) => (
  <Group gap="xs" wrap="nowrap">
    {[...tags].sort().map((tag) => {
      const isActive = currentTags.includes(tag);

      return (
        <Badge
          className={`checklist-tag-toggle-badge ${
            isActive ? "checklist-tag-toggle-badge--active" : "checklist-tag-toggle-badge--inactive"
          }`}
          key={tag}
          color={isActive ? "mc-primary" : "lightgray.6"}
          variant={isActive ? "filled" : "outline"}
          onClick={() =>
            onChange(isActive ? currentTags.filter((currentTag) => currentTag !== tag) : [...currentTags, tag])
          }
        >
          {tag}
        </Badge>
      );
    })}
  </Group>
);
